/*
 * Looseleafs are markdown files in ./posts. To publish one, add a file and
 * push. Nothing else needs editing: webpack's require.context picks it up at
 * build time and the slug comes from the filename.
 *
 * Filename:  YYYY-MM-DD-a-short-slug.md   ->  /looseleafs/a-short-slug
 *
 * Each file starts with a frontmatter block:
 *
 *   ---
 *   title: The title of the looseleaf
 *   summary: One or two lines shown in the list.
 *   ---
 *
 *   The body, in markdown.
 *
 * `date` is read from the filename, so it cannot drift from the ordering.
 *
 * Drafts go in ./drafts instead. webpack only scans ./posts, so a draft is
 * never bundled and cannot be read out of the shipped JavaScript. A
 * `draft: true` frontmatter flag would NOT do that: require.context bundles
 * every file it matches, and only hides it at render time.
 */

// Minimal frontmatter parser. Deliberately not gray-matter: that pulls in a
// YAML engine and needs a Buffer polyfill, which webpack 5 no longer provides.
const parseFrontmatter = (raw) => {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(raw.trim());
  if (!match) return { meta: {}, body: raw.trim() };

  const meta = {};
  match[1].split(/\r?\n/).forEach((line) => {
    const idx = line.indexOf(':');
    if (idx === -1) return;
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim().replace(/^["']|["']$/g, '');
    if (key) meta[key] = value;
  });

  return { meta, body: match[2].trim() };
};

// Accept a single-digit month or day (2026-06-8) and pad it, so a filename typo
// does not silently drop the post.
const FILENAME = /^\.\/(\d{4})-(\d{1,2})-(\d{1,2})-(.+)\.md$/;

const context = require.context('./posts', false, /\.md$/);

const posts = context.keys()
  .map((key) => {
    const match = FILENAME.exec(key);
    if (!match) {
      // Never drop a file in silence: a post that does not show up is much
      // harder to notice than a warning in the console.
      if (NODE_ENV !== 'production') {
        console.warn(
          `[looseleafs] Ignoring "${key.replace('./', '')}": expected `
          + 'YYYY-MM-DD-slug.md',
        );
      }
      return null;
    }
    const [, year, month, day, slug] = match;
    const date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    const { meta, body } = parseFrontmatter(context(key));

    return {
      slug,
      date,
      title: meta.title || slug,
      summary: meta.summary || '',
      body,
    };
  })
  .filter(Boolean)
  // Newest first. Dates are ISO so a string compare is correct. Never return a
  // non-zero value for equal dates: an inconsistent comparator leaves the order
  // of same-day posts up to the engine. Fall back to the slug so it is stable.
  .sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? 1 : -1;
    return a.slug < b.slug ? -1 : 1;
  });

export const getPost = (slug) => posts.find((p) => p.slug === slug);

export default posts;
